import React from 'react';
import {
    Show,
    SimpleShowLayout,
    useTranslate,
    EditButton,
    DeleteButton,
    useRecordContext,
    FunctionField,
    useRedirect,
    Link,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ElectricBolt,
    Bolt,
    DirectionsCar,
    CheckCircle,
    Cancel,
    Star,
    AttachMoney,
    Schedule,
    CalendarToday,
    Warning,
    ArrowBack,
    Info,
    Settings,
    History,
    Edit as EditIcon,
    DeleteOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FeeSchedule, FeeStructure } from '../../types/fee.types';

// Helper function to map entity types to resource names
const getEntityResource = (entityType: string): string => {
    switch (entityType) {
        case 'site':
            return 'sites';
        case 'load_group':
            return 'load-groups';
        case 'charger':
            return 'chargers';
        default:
            return 'sites';
    }
};

// Mobile Edit FAB component
const MobileEditFab = () => {
    const record = useRecordContext();
    const navigate = useNavigate();

    return (
        <Fab
            sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#009A75',
                zIndex: 1100,
                '&:hover': {
                    backgroundColor: '#00795C',
                },
            }}
            onClick={() => record && navigate(`/fee-schedules/${record.id}`)}
        >
            <EditIcon sx={{ color: '#FFFFFF' }} />
        </Fab>
    );
};

// Mobile Delete Section component
const MobileDeleteSection = () => {
    const translate = useTranslate();

    return (
        <Card sx={{ mb: 10, border: '1px solid', borderColor: 'error.main', overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DeleteOutline color="error" />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, color: 'error.main' }}>
                        {translate('ra.action.delete')}
                    </Typography>
                </Box>
                <DeleteButton
                    label={translate('ra.action.delete')}
                    variant="outlined"
                    sx={{
                        width: '100%',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        py: 1.5,
                        borderColor: '#D32F2F',
                        color: '#D32F2F',
                        '&:hover': {
                            borderColor: '#D32F2F',
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
};

// Page header component
const PageHeader = ({ isMobile }: { isMobile: boolean }) => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/fee-schedules')}
                    size="large"
                    sx={{
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        flexShrink: 0,
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {record?.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.show.subtitle')}
                    </Typography>
                </Box>
            </Box>

            {!isMobile && (
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <EditButton
                        label={translate('ra.action.edit')}
                        variant="contained"
                        sx={{
                            backgroundColor: '#00C853',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: '#00A843',
                                boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                            },
                        }}
                    />
                    <DeleteButton
                        label={translate('ra.action.delete')}
                        variant="outlined"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            borderColor: '#E0E0E0',
                            '&:hover': {
                                borderColor: '#D32F2F',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

const EntityDisplay = ({ record }: { record: FeeSchedule }) => {
    const translate = useTranslate();
    
    const entityIcons = {
        site: <ElectricBolt />,
        load_group: <Bolt />,
        charger: <DirectionsCar />,
    };
    
    const entityLabels = {
        site: translate('resources.fee-schedules.fields.site'),
        load_group: translate('resources.fee-schedules.fields.loadGroup'),
        charger: translate('resources.fee-schedules.fields.charger'),
    };
    

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.entityType')}
                    </Typography>
                    <span style={{ pointerEvents: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entityIcons[record.entityType]}
                            <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{entityLabels[record.entityType]}</Typography>
                        </Box>
                    </span>
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {record.entityType === 'site' ? translate('resources.sites.fields.name') :
                         record.entityType === 'load_group' ? translate('resources.load-groups.fields.name') :
                         record.entityType === 'charger' ? translate('resources.chargers.fields.name') :
                         translate('resources.fee-schedules.fields.entityName')}
                    </Typography>
                    {record.entity?.name ? (
                        <Link
                            to={`/${getEntityResource(record.entityType)}/${record.entityId}/show`}
                            sx={{
                                color: '#00C853',
                                textDecoration: 'none',
                                fontWeight: 500,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {record.entity.name}
                        </Link>
                    ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {entityLabels[record.entityType]}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const ScheduleStatusDisplay = ({ record }: { record: FeeSchedule }) => {
    const translate = useTranslate();
    
    const now = new Date();
    const effectiveFrom = new Date(record.effectiveFrom);
    const effectiveUntil = record.effectiveUntil ? new Date(record.effectiveUntil) : null;
    
    const isFuture = effectiveFrom > now;
    const isExpired = effectiveUntil && effectiveUntil < now;
    
    let status = translate('resources.fee-schedules.status.active');
    let statusColor: 'success' | 'warning' | 'error' | 'default' = 'success';
    let statusIcon = <CheckCircle />;
    
    if (!record.isActive) {
        status = translate('resources.fee-schedules.status.inactive');
        statusColor = 'default';
        statusIcon = <Cancel />;
    } else if (isFuture) {
        status = translate('resources.fee-schedules.status.future');
        statusColor = 'warning';
        statusIcon = <CalendarToday />;
    } else if (isExpired) {
        status = translate('resources.fee-schedules.status.expired');
        statusColor = 'error';
        statusIcon = <Cancel />;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.status')}
                    </Typography>
                    <span style={{ pointerEvents: 'none' }}>
                        <Chip
                            icon={statusIcon}
                            label={status}
                            color={statusColor}
                            variant="filled"
                            component="div"
                            clickable={false}
                            sx={{ cursor: 'default' }}
                        />
                    </span>
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.priority')}
                    </Typography>
                    <span style={{ pointerEvents: 'none' }}>
                        <Chip
                            icon={<Star />}
                            label={record.priority}
                            variant="outlined"
                            component="div"
                            clickable={false}
                            sx={{ cursor: 'default' }}
                        />
                    </span>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.effectiveFrom')}
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {effectiveFrom.toLocaleString()}
                    </Typography>
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.effectiveUntil')}
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {effectiveUntil ? effectiveUntil.toLocaleString() : translate('resources.fee-schedules.fields.indefinite')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const FeeConfigurationDisplay = ({ record }: { record: FeeSchedule }) => {
    const translate = useTranslate();
    
    // Prefer the live template's fee_structure when this schedule references a template.
    // The schedule's own stored feeStructure is a snapshot taken at create-time and is NOT
    // re-synced when the template is later edited (see fee-templates-pg.ts update handler),
    // so it can drift. Live billing reads via JOIN from backend_fee_templates so it stays
    // correct, but this admin page was reading the stale schedule snapshot directly.
    const feeStructure = record.templateId && record.template?.feeStructure
        ? record.template.feeStructure
        : record.feeStructure;
    const currency = record.currency || record.template?.currency || 'HKD';

    return (
        <Box>
            {record.templateId && record.template ? (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {translate('resources.fee-schedules.show.usingTemplate', { name: record.template.name })}
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {translate('resources.fee-schedules.fields.template')}
                            </Typography>
                            <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {record.template.name}
                            </Typography>
                        </Box>

                        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {translate('resources.fee-schedules.fields.currency')}
                            </Typography>
                            <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {currency}
                            </Typography>
                        </Box>
                    </Box>

                    {record.template.description && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {translate('resources.fee-schedules.fields.description')}
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {record.template.description}
                            </Typography>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('resources.fee-schedules.show.customConfiguration')}
                    </Alert>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {translate('resources.fee-schedules.fields.currency')}
                        </Typography>
                        <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {currency}
                        </Typography>
                    </Box>
                </Box>
            )}
            
            {feeStructure ? (
                <FeeStructureDisplay feeStructure={feeStructure} currency={currency} />
            ) : (
                <Alert severity="error">
                    {translate('resources.fee-schedules.show.noFeeStructure')}
                </Alert>
            )}
        </Box>
    );
};

const FeeStructureDisplay = ({ feeStructure, currency }: { feeStructure: FeeStructure; currency: string }) => {
    const translate = useTranslate();
    
    const componentIcons = {
        sessionFee: <AttachMoney />,
        energyFee: <ElectricBolt />,
        timeFee: <Schedule />,
        idleFee: <Warning />,
        reservationFee: <CalendarToday />,
    };

    const componentLabels = {
        sessionFee: translate('resources.fee-templates.components.sessionFee'),
        energyFee: translate('resources.fee-templates.components.energyFee'),
        timeFee: translate('resources.fee-templates.components.timeFee'),
        idleFee: translate('resources.fee-templates.components.idleFee'),
        reservationFee: translate('resources.fee-templates.components.reservationFee'),
    };
    
    const components = feeStructure.components || {};
    const enabledComponents = Object.entries(components).filter(([_, component]: [string, any]) => component?.enabled);
    
    if (enabledComponents.length === 0) {
        return (
            <Alert severity="info">
                {translate('resources.fee-schedules.show.noEnabledComponents')}
            </Alert>
        );
    }

    return (
        <Box>
            {enabledComponents.map(([key, component]) => (
                <Box key={key} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {componentIcons[key as keyof typeof componentIcons]}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {componentLabels[key as keyof typeof componentLabels]}
                        </Typography>
                        <span style={{ pointerEvents: 'none' }}>
                            <Chip 
                                label={translate('resources.fee-templates.components.enabled')} 
                                size="small" 
                                color="success"
                                component="div"
                                clickable={false}
                                sx={{ cursor: 'default' }}
                            />
                        </span>
                    </Box>
                    <ComponentDetails component={component} componentType={key} currency={currency} />
                </Box>
            ))}
            
            {feeStructure.specialDates && feeStructure.specialDates.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        {translate('resources.fee-templates.components.specialDates')}
                    </Typography>
                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('resources.fee-templates.fields.date')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.name')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.rateMultiplier')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.components')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {feeStructure.specialDates.map((specialDate, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{specialDate.date}</TableCell>
                                        <TableCell>{specialDate.name}</TableCell>
                                        <TableCell>
                                            {specialDate.rateMultiplier 
                                                ? `${(specialDate.rateMultiplier * 100).toFixed(0)}%`
                                                : '-'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {specialDate.components?.join(', ') || 'All'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};

const ComponentDetails = ({ component, componentType, currency }: { component: any; componentType: string; currency: string }) => {
    const translate = useTranslate();
    
    switch (componentType) {
        case 'sessionFee':
            return (
                <Box>
                    <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.amount')}:</strong> {component.amount} {currency}</Typography>
                    {component.description && (
                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.description')}:</strong> {component.description}</Typography>
                    )}
                </Box>
            );
            
        case 'energyFee':
            return (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('resources.fee-templates.components.energyRates')}
                    </Typography>
                    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('resources.fee-templates.fields.rateName')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.rateAmount')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.unit')}</TableCell>
                                    <TableCell>{translate('resources.fee-templates.fields.timeWindows')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {component.rates?.map((rate: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>{rate.name}</TableCell>
                                        <TableCell>{rate.rate} {currency}</TableCell>
                                        <TableCell>{rate.unit}</TableCell>
                                        <TableCell>
                                            {rate.timeWindows?.length > 0
                                                ? rate.timeWindows.map((tw: any) =>
                                                    `${tw.days.join(', ')}: ${tw.start}-${tw.end}`
                                                  ).join('; ')
                                                : translate('resources.fee-templates.fields.allTimes')
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
            
        case 'timeFee':
            return (
                <Box>
                    {component.rates?.map((rate: any, index: number) => (
                        <Typography key={index} sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{rate.name ? `${rate.name}: ` : ''}{translate('resources.fee-templates.fields.rate')}:</strong> {rate.rate} {currency} per {component.unit || 'minute'}</Typography>
                    ))}
                    {component.freeMinutes > 0 && (
                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.freeMinutes')}:</strong> {component.freeMinutes}</Typography>
                    )}
                    {component.maxChargeMinutes > 0 && (
                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.maxChargeMinutes')}:</strong> {component.maxChargeMinutes}</Typography>
                    )}
                </Box>
            );

        case 'idleFee':
            return (
                <Box>
                    <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.graceMinutes')}:</strong> {component.graceMinutes}</Typography>
                    <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.rate')}:</strong> {component.rate} {currency} per {component.unit}</Typography>
                    {component.maxPerSession && (
                        <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.maxPerSession')}:</strong> {component.maxPerSession}</Typography>
                    )}
                </Box>
            );

        case 'reservationFee':
            return (
                <Box>
                    <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}><strong>{translate('resources.fee-templates.fields.amount')}:</strong> {component.amount} {currency}</Typography>
                </Box>
            );

        default:
            return <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>Unknown component type</Typography>;
    }
};


const MetadataDisplay = ({ record }: { record: FeeSchedule }) => {
    const translate = useTranslate();

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.fee-schedules.fields.createdAt')}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {new Date(record.createdAt).toLocaleString()}
                </Typography>
            </Box>

            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.fee-schedules.fields.updatedAt')}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {new Date(record.updatedAt).toLocaleString()}
                </Typography>
            </Box>

            {record.createdBy && (
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.fee-schedules.fields.createdBy')}
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {record.createdBy}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export const FeeSchedulesShow = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ position: 'relative' }}>
            <Show
                title={false}
                actions={false}
                component="div"
                sx={{
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    backgroundColor: 'transparent',
                    '& .RaShow-main': {
                        maxWidth: '100%',
                        overflowX: 'hidden',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >
                <SimpleShowLayout
                    sx={{
                        padding: { xs: 0, sm: 'inherit' },
                        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                            px: { xs: 0, sm: 2 },
                            py: { xs: 1, sm: 2 },
                        },
                        '& .MuiPaper-root': {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        },
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
                        <PageHeader isMobile={isMobile} />

                        {/* Entity Information */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Info sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.show.entityInfo')}
                                    </Typography>
                                </Box>

                                <FunctionField
                                    render={(record: FeeSchedule) => (
                                        <span style={{ pointerEvents: 'none' }}>
                                            <EntityDisplay record={record} />
                                        </span>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        {/* Schedule Status */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Schedule sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.show.scheduleStatus')}
                                    </Typography>
                                </Box>

                                <FunctionField
                                    render={(record: FeeSchedule) => (
                                        <span style={{ pointerEvents: 'none' }}>
                                            <ScheduleStatusDisplay record={record} />
                                        </span>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        {/* Fee Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Settings sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.show.feeConfiguration')}
                                    </Typography>
                                </Box>

                                <FunctionField
                                    render={(record: FeeSchedule) => (
                                        <span style={{ pointerEvents: 'none' }}>
                                            <FeeConfigurationDisplay record={record} />
                                        </span>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        {/* Metadata */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <History sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.fee-schedules.show.metadata')}
                                    </Typography>
                                </Box>

                                <FunctionField
                                    render={(record: FeeSchedule) => (
                                        <span style={{ pointerEvents: 'none' }}>
                                            <MetadataDisplay record={record} />
                                        </span>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        {isMobile && <MobileDeleteSection />}
                    </Box>
                </SimpleShowLayout>
                {isMobile && <MobileEditFab />}
            </Show>
        </Box>
    );
};